import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ApplicationButtonText,
  ApplicationCardContent,
  ApplicationsCode,
  ApplicationsCodeTitle,
  ApplicationsList as StyledApplicationsList,
  ApplicationsListContainer,
  ApplicationsListContent,
  ApplicationsListHeader,
  CardBody,
  CardFooter,
  CardHeader,
} from "./applicationsList.styles";
import { TitleH1, TitleH2 } from "../../../../shared/native/typography.styles";
import LinkCard from "../../../../shared/cards/linkCard/LinkCard";
import { useAppDispatch, useAppSelector } from "../../../../../core/hooks/redux";
import { resetHighEducationState } from "../../../../../store/modules/applications/highEducation/highEducationSlice";
import {
  fetchGetNotificationsInfo,
  fetchNotificationsChangeRequest,
} from "../../../../../store/modules/notifications/notificationsThunk";
import formatDate from "../../../../../core/utils/formatDate";
import degreeType from "../../../../../core/types/degreeType";
import colors from "../../../../../core/styles/colors";
import { PrimaryButton } from "../../../../shared/native/button.styles";

const ApplicationsList: React.FC<{ degree: degreeType }> = ({ degree }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { notificationsState } = useAppSelector((state) => state.modulesState);
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    dispatch(fetchGetNotificationsInfo());
  }, []);

  const changeRequest = async (): Promise<void> => {
    setIsLoading(true);
    const response = await dispatch(fetchNotificationsChangeRequest()).finally(() =>
      setIsLoading(false)
    );
    const payload = response.payload as { error: boolean };
    if (!payload.error) {
      navigate("/notifications/high-education/general");
    }
  };

  const applicationClickHandler = (isNew: boolean, applicationId?: string): void => {
    if (isNew) {
      dispatch(resetHighEducationState());
      navigate(`/notifications/${degree}-education/general`);
      return;
    }
    navigate(`/notifications/${degree}-education/${applicationId}`);
  };

  const applications = {
    high: notificationsState?.info?.vpo?.applications,
    additional: notificationsState?.info?.dpo?.applications,
    postgraduate: notificationsState?.info?.asp?.applications,
  }?.[degree];

  const status = {
    high: notificationsState.info?.vpo?.status,
    additional: notificationsState.info?.asp?.status,
    postgraduate: null,
  }[degree];

  const notificationCount = {
    high: notificationsState.info?.vpo?.common,
    additional: notificationsState.info?.dpo?.common,
    postgraduate: notificationsState.info?.asp?.common,
  }?.[degree];

  return (
    <ApplicationsListContainer>
      {degree === "high" && notificationsState?.info?.vpo?.code_ss && (
        <ApplicationsCode>
          <ApplicationsCodeTitle style={{ color: colors.red.general }}>
            Уникальный код абитуриента ЕПГУ
          </ApplicationsCodeTitle>

          <ApplicationsCodeTitle>{notificationsState?.info?.vpo?.code_ss}</ApplicationsCodeTitle>
        </ApplicationsCode>
      )}

      <ApplicationsListContent>
        <ApplicationsListHeader>
          <TitleH1>Уведомления</TitleH1>
          {degree === "high" && (
            <PrimaryButton disabled={isLoading} onClick={changeRequest}>
              <ApplicationButtonText>Заключить договор</ApplicationButtonText>
            </PrimaryButton>
          )}
        </ApplicationsListHeader>

        <StyledApplicationsList>
          {status && <TitleH2>{status}</TitleH2>}
          <LinkCard
            notificationsCount={notificationCount}
            onClick={(): void => applicationClickHandler(true)}
          >
            <ApplicationCardContent isNew>
              {!!notificationCount && <span className="chip">{notificationCount}</span>}

              <p>Общие документы</p>
            </ApplicationCardContent>
          </LinkCard>

          {!!applications &&
            applications.map((application) => (
              <LinkCard
                key={application.id}
                notificationsCount={application.count}
                onClick={(): void => applicationClickHandler(false, application.id)}
              >
                {!!application.count && <span className="chip">{application.count}</span>}
                <ApplicationCardContent>
                  <CardHeader>
                    <b>{["high", "postgraduate"].includes(degree) ? application.level : ""}</b>
                    <p>{formatDate(application.datetime.split(" ")?.[0])}</p>
                  </CardHeader>
                  <CardBody>
                    <b>{application.program}</b>
                    <p>{application.form}</p>
                    <p>{["high", "postgraduate"].includes(degree) ? application.financing : ""}</p>
                  </CardBody>
                  <CardFooter>
                    <p>{application.status}</p>
                  </CardFooter>
                </ApplicationCardContent>
              </LinkCard>
            ))}
        </StyledApplicationsList>
      </ApplicationsListContent>
    </ApplicationsListContainer>
  );
};

export default ApplicationsList;
